import React from "react";
import styled from "styled-components";
import { palette } from "../../styles/palette";
import { typography } from "../../styles/typography";
import { ReviewInfo, getImageUrl } from "@/api/soollogApi";
import { useNavigate } from "react-router-dom";
import { fontFamily } from "@/styles/font";

interface ReviewListProps {
    reviews: ReviewInfo[];
    isLoading: boolean;
    error: string | null;
    onLoadMore: () => void;
    hasMore: boolean;
}

const ReviewList: React.FC<ReviewListProps> = ({ reviews, isLoading, error, onLoadMore, hasMore }) => {
    const navigate = useNavigate();

    // 리뷰 아이템 클릭 핸들러
    const handleReviewClick = (review: ReviewInfo) => {
        navigate(`/mypage/review/${review.altKey}`, {
            state: { reviewData: review }
        });
    };

    // 검색 페이지로 이동하는 함수
    const goToSearch = () => {
        navigate("/search");
    };

    // 로딩 상태 표시
    if (isLoading && reviews.length === 0) {
        return <LoadingMessage>리뷰를 불러오는 중입니다...</LoadingMessage>;
    }

    // 에러 상태 표시
    if (error) {
        return <ErrorMessage>{error}</ErrorMessage>;
    }

    // 데이터가 없는 경우
    if (reviews.length === 0) {
        return (
            <EmptyState>
                <EmptyMessage>아직 술로그가 없네요 ✍️</EmptyMessage>
                <EmptySubMessage>첫 번째 기록을 남겨볼까요?</EmptySubMessage>
                <SearchButton onClick={goToSearch}>술 검색하고 리뷰 남기기</SearchButton>
            </EmptyState>
        );
    }

    // 날짜 포맷 함수
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getDate()).padStart(
            2,
            "0"
        )} 기록`;
    };

    return (
        <Container>
            {reviews.map((review) => (
                <ItemCard key={review.altKey} onClick={() => handleReviewClick(review)}>
                    <ItemImage $imageUrl={review.reviewImageInfoList[0]?.imageUrl} />
                    <ItemInfoContainer>
                        <InfoSection>
                            <ItemTitle>{review.reviewProductInfo.productName}</ItemTitle>
                            <ItemSubtitle>{formatDate(review.reviewDate)}</ItemSubtitle>
                        </InfoSection>
                        <RatingContainer>
                            {Array.from({ length: 5 }).map((_, index) => (
                                <StarIcon key={index} $filled={index < review.rating} />
                            ))}
                        </RatingContainer>
                        <CategoryTag>{review.reviewProductInfo.type}</CategoryTag>
                    </ItemInfoContainer>
                </ItemCard>
            ))}

            {isLoading && reviews.length > 0 && <LoadingMore>더 불러오는 중...</LoadingMore>}

            {hasMore && !isLoading && <LoadMoreButton onClick={onLoadMore}>더 불러오기</LoadMoreButton>}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 15px;
`;

const ItemCard = styled.div`
    width: 100%;
    height: 100px;
    position: relative;
    display: flex;
    background: ${palette.white};
    border-radius: 8px;
    box-sizing: border-box;
    margin-bottom: 1px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
        background: #f9f9f9;
    }
`;

const ItemImage = styled.div<{ $imageUrl?: string }>`
    position: relative;
    width: 28%;
    height: 100%;
    border-radius: 12px;
    background: ${(props) =>
        props.$imageUrl ? `url(${getImageUrl(props.$imageUrl)})` : `url("/images/default-image.png")`};
    background-size: cover;
    background-position: center;
    transition: transform 0.2s ease-in-out;
`;

const ItemInfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 10px;
    gap: 10px;
    position: relative;
    width: 72%;
    height: 100%;
`;

const InfoSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0px;
    gap: 8px;
    width: 100%;
`;

const ItemTitle = styled.div`
    ${typography.title2}
    color: ${palette.gray[900]};
`;

const ItemSubtitle = styled.div`
    ${typography.body2}
    font-weight: 700;
    color: ${palette.gray[700]};
`;

const RatingContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0px;
    gap: 3px;
`;

const StarIcon = styled.div<{ $filled: boolean }>`
    width: 20px;
    height: 20px;
    background-color: ${(props) => (props.$filled ? "#ffc700" : palette.gray[300])};
    clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
`;

const CategoryTag = styled.div`
    ${typography.tag}
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 1px 8px;
    gap: 10px;
    position: absolute;
    min-width: 70px;
    width: auto;
    height: 20px;
    right: 10px;
    bottom: 10px;
    border: 1px solid #ffc700;
    border-radius: 10px;
    color: #ffc700;
    white-space: nowrap;
    overflow: hidden;
`;

const LoadingMessage = styled.div`
    ${typography.title3}
    color: ${palette.gray[700]};
    text-align: center;
    padding: 30px 0;
`;

const ErrorMessage = styled.div`
    ${typography.title3}
    color: ${palette.red};
    text-align: center;
    padding: 30px 0;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
`;

const EmptyMessage = styled.p`
    font-family: ${fontFamily.base};
    font-size: 18px;
    font-weight: 600;
    color: ${palette.gray[800]};
    margin-bottom: 8px;
`;

const EmptySubMessage = styled.p`
    font-family: ${fontFamily.base};
    font-size: 14px;
    color: ${palette.gray[600]};
    margin-bottom: 24px;
`;

const SearchButton = styled.button`
    background-color: ${palette.primary};
    color: white;
    font-family: ${fontFamily.base};
    font-size: 14px;
    font-weight: 600;
    padding: 12px 20px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: ${palette.primary}ee;
    }
`;

const LoadMoreButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 120px;
    height: 40px;
    background: #ffffff;
    border: 1px solid #007f73;
    border-radius: 8px;
    font-family: ${fontFamily.base};
    font-weight: 500;
    font-size: 14px;
    color: #007f73;
    margin: 24px auto 0;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: #f2f9f8;
    }
`;

const LoadingMore = styled.div`
    ${typography.body2}
    color: ${palette.gray[600]};
    text-align: center;
    padding: 10px 0;
`;

export default ReviewList;
